import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  Autocomplete,
  FormControl,
  FormHelperText,
  ListItemText,
  TextField,
} from "@mui/material";
import { Grid, Chip, Checkbox } from "@mui/material";

import {
  BILLING_CODES_99458,
  billingCode99458CountMap,
  RPM_BILLING_CODES_OPTIONS,
  ADDITIONAL_BILLING_CODES,
  DIAGNOSIS_CODES_BY_RPM_BILLING_CODES,
  BILLING_CODE_99458_VARIATION_BY_COUNT,
  IN_OFFICE_DIAGNOSIS_CODES_BY_BILLING_CODES,
  BILLING_CODE_99458,
  BILLING_CODE_DESCRIPTION_MAP,
  BILLING_CODE_G2066,
  DIAGNOSIS_CODE_DESCRIPTION_MAP,
  CLINIC_NAMES,
} from "static/constants";

import {
  setSelectedBillingCodes,
  setRemoteBillingCodeMapper,
  setShowReportTemplateUpdateAlert,
  setPatientInfoToUpdate,
} from "app/store/manualUpload/slice/manualUploadSlice";

import { isValidArray } from "utils/array";
import {
  checkValidBillingCodes,
  getRemoteBillingDiagnosisMapper,
} from "utils/billingCode.util";

const BillingDiagnosisCodeInput = ({ fields, onUpdate, isIDTFBilling }) => {
  const {
    reportTemplates,
    isSubmitClicked,
    transmissionType,
    isExistingPatient,
    selectedBillingCodes,
    remoteBillingCodeMapper,
  } = useSelector((state) => state.manualUpload);

  const { rpmPatientBilling } = useSelector((state) => state.rpmPatient);

  const { currentUser } = useSelector((state) => state.auth);
  const allowedBillingCodes = currentUser?.user_prefs?.allowed_billing_codes;
  const isPuebloCardiology = fields?.account === CLINIC_NAMES.PUEBLO;

  const dispatch = useDispatch();

  useEffect(() => {
    const billingCodeMapper = getRemoteBillingDiagnosisMapper(isIDTFBilling);

    dispatch(setRemoteBillingCodeMapper(billingCodeMapper));

    if (!fields.isRpmDevice) {
      const filteredBillingCodes = fields.billingDiagnosisCodes
        ?.map(({ billingCode }) => billingCode)
        ?.filter((billingCode) => billingCode in billingCodeMapper);

      handleBillingCodesSelect(filteredBillingCodes);
    }
  }, [fields.isRpmDevice, fields?.report_billing_date, isIDTFBilling]);

  useEffect(() => {
    if (isValidArray(rpmPatientBilling)) {
      const billingCodes = rpmPatientBilling?.map(({ billing_code }) =>
        billing_code.toString()
      );

      let updatedBillingCodes = billingCodes;
      const billingCode99458Count = updatedBillingCodes?.filter(
        (billingCode) => billingCode === BILLING_CODE_99458
      ).length;

      if (billingCode99458Count > 0) {
        updatedBillingCodes = updatedBillingCodes.filter(
          (billingCodes) => billingCodes !== BILLING_CODE_99458
        );
        const new99458billingCode =
          billingCode99458Count <= 6
            ? BILLING_CODE_99458_VARIATION_BY_COUNT[billingCode99458Count]
            : BILLING_CODE_99458_VARIATION_BY_COUNT[6];
        updatedBillingCodes = [...updatedBillingCodes, ...new99458billingCode];
      }

      handleBillingCodesSelect(updatedBillingCodes, true);
    }
  }, [rpmPatientBilling]);

  const getDiagnosisCodesByBillingCode = (billingCode, isRpmDevice) => {
    const isRpmDeviceSelected = fields.isRpmDevice || isRpmDevice;
    const diagnosisCodeMapper =
      transmissionType === "inOffice"
        ? IN_OFFICE_DIAGNOSIS_CODES_BY_BILLING_CODES
        : isRpmDeviceSelected
          ? DIAGNOSIS_CODES_BY_RPM_BILLING_CODES
          : remoteBillingCodeMapper;

    return diagnosisCodeMapper?.[billingCode] || [];
  };

  const getDefaultDiagnosisCode = (billingCode, isRpmDevice = false) => {
    const diagnosisCodes = getDiagnosisCodesByBillingCode(
      billingCode,
      isRpmDevice
    );

    return diagnosisCodes?.[0] || "";
  };

  const handleBillingCodesSelect = (billingCodes, isRpmDevice) => {
    const updatedCodes = {};

    billingCodes.forEach((billingCode) => {
      const oldCode = fields.billingDiagnosisCodes?.find(
        (code) => code.billingCode === billingCode
      );

      const diagnosisCode = oldCode
        ? oldCode.diagnosisCode
        : getDefaultDiagnosisCode(billingCode, isRpmDevice);

      const billingDiagnosisObject = { billingCode, diagnosisCode };

      if (BILLING_CODES_99458.includes(billingCode)) {
        billingDiagnosisObject.count = billingCode99458CountMap[billingCode];
      }

      updatedCodes[billingCode] = billingDiagnosisObject;

      const additionalCodes = ADDITIONAL_BILLING_CODES[billingCode];

      if (!isIDTFBilling && additionalCodes) {
        additionalCodes.forEach((code) => {
          updatedCodes[code] = { billingCode: code, diagnosisCode };
        });
      }
    });

    if (reportTemplates.length > 0) {
      dispatch(setShowReportTemplateUpdateAlert(true));
      dispatch(
        setPatientInfoToUpdate({
          key: "billingDiagnosisCodes",
          newValue: updatedCodes,
          currentValue: selectedBillingCodes,
        })
      );
    } else {
      dispatch(setSelectedBillingCodes(Object.keys(updatedCodes)));
      onUpdate({ billingDiagnosisCodes: Object.values(updatedCodes) });
    }
  };

  const handleDiagnosisCodeSelect = (index, billingDiagnosisObject) => {
    const billingDiagnosisCopy = fields.billingDiagnosisCodes;
    billingDiagnosisCopy[index] = billingDiagnosisObject;

    const { billingCode, diagnosisCode } = billingDiagnosisObject;

    const additionalCodes = ADDITIONAL_BILLING_CODES[billingCode];

    if (!isIDTFBilling && additionalCodes) {
      additionalCodes.forEach((code) => {
        const codeIndex = billingDiagnosisCopy.findIndex(
          (item) => item.billingCode === code
        );

        billingDiagnosisCopy[codeIndex] = { billingCode: code, diagnosisCode };
      });
    }

    onUpdate({ billingDiagnosisCodes: billingDiagnosisCopy });
  };

  const renderMultipleBillingCodes = () => {
    const billingCodesOptions = !fields.isRpmDevice
      ? Object.keys(
          transmissionType === "remote"
            ? getRemoteBillingDiagnosisMapper(isIDTFBilling)
            : IN_OFFICE_DIAGNOSIS_CODES_BY_BILLING_CODES
        )
      : RPM_BILLING_CODES_OPTIONS;

    const filteredBillingCodesOptions = (billingCodesOptions ?? []).filter(
      (code) =>
        code !== BILLING_CODE_G2066 &&
        (!isValidArray(allowedBillingCodes) ||
          allowedBillingCodes.includes(code))
    );

    const isEmpty =
      isSubmitClicked &&
      selectedBillingCodes.length === 0 &&
      !fields.isRpmDevice;

    const hasInvalidCodes = checkValidBillingCodes(
      selectedBillingCodes,
      isIDTFBilling
    );

    return (
      <FormControl fullWidth variant="outlined" style={{ width: "100%" }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          id="billingCodes"
          name="billingCodes"
          value={selectedBillingCodes || []}
          options={filteredBillingCodesOptions || []}
          filterOptions={(options, params) => {
            const { inputValue } = params;
            if (!inputValue) {
              return options;
            }

            const lowerInputValue = inputValue.toLowerCase();
            return options.filter((option) => {
              const code = option.toLowerCase();
              const description = (
                BILLING_CODE_DESCRIPTION_MAP[option] || ""
              ).toLowerCase();
              return (
                code.includes(lowerInputValue) ||
                description.includes(lowerInputValue)
              );
            });
          }}
          getOptionLabel={(option) => option}
          noOptionsText="No options"
          data-action="billing-diagnosis-code-billing-codes-autocomplete"
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <li
                key={key}
                {...optionProps}
                data-action={`billing-diagnosis-code-billing-option-${option}`}
              >
                <Checkbox
                  style={{ marginRight: 8 }}
                  checked={selected}
                  data-action={`billing-diagnosis-code-billing-checkbox-${option}`}
                />
                <ListItemText
                  primary={option}
                  secondary={BILLING_CODE_DESCRIPTION_MAP[option]}
                  primaryTypographyProps={{
                    style: { whiteSpace: "normal" },
                  }}
                  secondaryTypographyProps={{
                    style: { whiteSpace: "normal" },
                  }}
                />
              </li>
            );
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option}
                  style={{ margin: "1%", backgroundColor: "#ececec" }}
                  data-action={`billing-diagnosis-code-billing-chip-${option}`}
                  {...tagProps}
                />
              );
            })
          }
          onChange={(e, value) => {
            handleBillingCodesSelect(value);
          }}
          style={{ width: "100%" }}
          renderInput={(params) => (
            <>
              <TextField
                {...params}
                label="Billing Codes"
                variant="outlined"
                required
                error={(isSubmitClicked && isEmpty) || hasInvalidCodes}
                data-text="billing-diagnosis-code-billing-codes-field"
              />

              {((isSubmitClicked && isEmpty) || hasInvalidCodes) && (
                <FormHelperText error>{`${
                  isEmpty
                    ? "Billing Codes is/are required."
                    : "Invalid billing codes provided."
                }`}</FormHelperText>
              )}
            </>
          )}
        />
      </FormControl>
    );
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          style={{
            boxSizing: "border-box",
            marginTop: isExistingPatient ? "-12px" : 0,
          }}
        >
          {renderMultipleBillingCodes()}
        </Grid>
      </Grid>

      {isValidArray(selectedBillingCodes) &&
        fields.billingDiagnosisCodes.map((code, index) => (
          <Grid container className="mt-3" key={`${code.billingCode}_${index}`}>
            <Grid item xs={2} className="gridItem">
              <span
                className="gridItem-title"
                data-label="billing-diagnosis-code-billing-code-label"
              >
                BILLING CODE
              </span>
              <span
                className="gridItem-subTitle"
                data-label={`billing-diagnosis-code-billing-code-value-${code.billingCode}`}
              >
                {code.billingCode}
              </span>
            </Grid>

            <Grid item xs={10}>
              <FormControl
                fullWidth
                variant="outlined"
                error={
                  isSubmitClicked && !code.diagnosisCode && !fields.isRpmDevice
                }
                className="mt-2"
              >
                <Autocomplete
                  freeSolo={isPuebloCardiology}
                  id={`diagnosisCode-${index}`}
                  value={code.diagnosisCode}
                  options={
                    getDiagnosisCodesByBillingCode(code.billingCode) || []
                  }
                  disabled={Object.values(ADDITIONAL_BILLING_CODES)
                    ?.flat()
                    ?.includes(code.billingCode)}
                  getOptionLabel={(option) => option}
                  filterOptions={(options, params) => {
                    const { inputValue } = params;
                    if (!inputValue) {
                      return options;
                    }

                    const lowerInputValue = inputValue.toLowerCase();
                    const filtered = options.filter((option) => {
                      const diagCode = option.toLowerCase();
                      const description = (
                        DIAGNOSIS_CODE_DESCRIPTION_MAP[option] || ""
                      ).toLowerCase();
                      return (
                        diagCode.includes(lowerInputValue) ||
                        description.includes(lowerInputValue)
                      );
                    });

                    if (isPuebloCardiology) {
                      const exactMatch = options.some(
                        (option) => option.toLowerCase() === lowerInputValue
                      );
                      if (!exactMatch) {
                        filtered.push(`Add "${inputValue}"`);
                      }
                    }

                    return filtered;
                  }}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    const addMatch = option.match(/^Add "(.+)"$/);
                    const isCustomAdd = !!addMatch;
                    const displayCode = isCustomAdd ? addMatch[1] : option;
                    return (
                      <li key={key} {...optionProps}>
                        <ListItemText
                          primary={
                            isCustomAdd ? `Add "${displayCode}"` : option
                          }
                          secondary={
                            isCustomAdd
                              ? "Custom code"
                              : DIAGNOSIS_CODE_DESCRIPTION_MAP[option]
                          }
                          primaryTypographyProps={{
                            style: { whiteSpace: "normal" },
                          }}
                          secondaryTypographyProps={{
                            style: { whiteSpace: "normal" },
                          }}
                        />
                      </li>
                    );
                  }}
                  onChange={(e, value) => {
                    const addMatch = value?.match(/^Add "(.+)"$/);
                    const resolvedValue = addMatch ? addMatch[1] : value;
                    const billingDiagnosisObject = {
                      billingCode: code.billingCode,
                      diagnosisCode: resolvedValue?.toUpperCase() || "",
                    };
                    handleDiagnosisCodeSelect(index, billingDiagnosisObject);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Diagnosis Code"
                      required={!fields.isRpmDevice}
                      error={
                        isSubmitClicked &&
                        !code.diagnosisCode &&
                        !fields.isRpmDevice
                      }
                      data-text={`billing-diagnosis-code-diagnosis-${code.billingCode}-field`}
                      inputProps={{
                        ...params.inputProps,
                        maxLength: 15,
                      }}
                    />
                  )}
                  noOptionsText="No options"
                  data-action={`billing-diagnosis-code-diagnosis-${code.billingCode}-autocomplete`}
                />
                {isSubmitClicked && !code.diagnosisCode && (
                  <FormHelperText error>
                    Diagnosis Code is required.
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        ))}
    </>
  );
};

export default BillingDiagnosisCodeInput;
